import { useEffect, useRef } from 'react';
import {
  POS_UNAVAILABLE_ANALYTICS_EVENT,
  POS_UNAVAILABLE_COPY,
  POS_UNAVAILABLE_SUBMIT_REASON_ID,
} from './constants';
import { useStoreUnavailable, useStoreAvailabilityState } from './hooks';
import { PosUnavailableBanner } from './pos-unavailable-banner';
import * as Styled from './styles';
import { useSelector } from '@/redux';
import { safeHeapTrack } from '@/utils/analytics';

/**
 * Checkout-side counterpart to the menu banner (TEAM-14640, extended TEAM-15703): repeats
 * the "can't accept orders" banner above the order summary, renders the helper text that
 * the disabled Place Order button points to via `aria-describedby`, and fires the analytics
 * event once when the gate blocks the diner. Now covers both POS refusal and operator-paused
 * / disabled stores. Renders nothing when the store is accepting orders, so checkout is
 * byte-for-byte unchanged in the normal case.
 */
export const PosUnavailableCheckoutNotice = () => {
  const location = useSelector((state) => state.app.locations.detail);
  const isUnavailable = useStoreUnavailable(location);
  const availabilityState = useStoreAvailabilityState(location);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (isUnavailable && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      safeHeapTrack(POS_UNAVAILABLE_ANALYTICS_EVENT, {
        locationId: location?.id,
        reason: availabilityState,
      });
    }
  }, [isUnavailable, availabilityState, location?.id]);

  if (!isUnavailable) {
    return null;
  }

  return (
    <>
      <PosUnavailableBanner location={location} />
      <Styled.CheckoutHelperText id={POS_UNAVAILABLE_SUBMIT_REASON_ID}>
        {POS_UNAVAILABLE_COPY.checkoutMessage}
      </Styled.CheckoutHelperText>
    </>
  );
};
